/**
 * Typed wrappers for the `/api/builder/*` and `/api/auth/*` endpoints.
 *
 * Each function maps to a single FastAPI route in `src/builder/api/routes/`.
 * Response bodies are typed against `@/types`. The frontend never builds
 * URLs ad-hoc — page components import these helpers exclusively.
 */

import {
  createInFlightDeduper,
  isResolvedDateWindow,
  type DatePresetCompute,
  type RangeHistogram,
} from "@pervasivemind/dashboard-render";

import { api, HttpError } from "@/lib/api";
import type {
  AIConfig,
  AiHomeViewsCheck,
  AiHomeViewsConflict,
  DashboardConfigV2,
  DuckDBColumnInfo,
  DuckDBTableInfo,
  FilesConfig,
  LoginResponse,
  MasterFilesConfig,
  Project,
  ProjectListResponse,
  ReportConfig,
  ReportDraft,
  ReportDraftListResponse,
  User,
  ValidationResult,
  AiHomeTime,
} from "@/types";

// ── Auth ──────────────────────────────────────────────────

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>(
      "/api/auth/login",
      { email, password },
      { anonymous: true },
    ),
  me: () => api.get<User>("/api/auth/me"),
};

// ── Drafts CRUD ───────────────────────────────────────────

export interface DraftCreatePayload {
  key: string;
  /** Optional template slug — server seeds the configs with the template's content. */
  template_slug?: string | null;
  /** Optional project to link this draft to. */
  project_id?: string | null;
  report_config?: ReportConfig;
  dashboard_config?: DashboardConfigV2 | Record<string, unknown>;
  ai_config?: AIConfig;
}

export interface DraftUpdatePayload {
  project_id?: string | null;
  report_config?: ReportConfig;
  dashboard_config?: DashboardConfigV2 | Record<string, unknown>;
  ai_config?: AIConfig;
  /** The caller saw the saved views this live save breaks and saves anyway. */
  confirm_broken_views?: boolean;
}

/** The 409 of a live write that would break saved AI Home views, or
 *  ``null`` for any other failure (which keeps its own message). */
export function viewsConflictOf(err: unknown): AiHomeViewsConflict | null {
  if (!(err instanceof HttpError) || err.status !== 409) return null;
  const detail = (err.detail as { detail?: unknown } | undefined)?.detail;
  if (detail === null || typeof detail !== "object") return null;
  const code = (detail as { code?: unknown }).code;
  return code === "ai_home_views_would_break" || code === "ai_home_views_check_failed"
    ? (detail as AiHomeViewsConflict)
    : null;
}

export interface DraftHistoryEntry {
  id: string;
  created_at: string;
  created_by: string | null;
  report_config: ReportConfig;
  dashboard_config: DashboardConfigV2 | Record<string, unknown>;
  ai_config: AIConfig;
}

/** One persisted AI-insight execution (ai_analysis_snapshots row). */
export interface AiExecution {
  id: string;
  as_of_date: string;
  week_iso: string;
  model: string;
  duration_ms: number | null;
  data_context_size: number | null;
  insights_count: number;
  regenerated_count: number;
  created_at: string;
}

export interface AiExecutionListResponse {
  executions: AiExecution[];
  total: number;
}

export const draftsApi = {
  list: (statusFilter?: string) =>
    api.get<ReportDraftListResponse>("/api/builder", {
      query: { status_filter: statusFilter },
    }),
  get: (key: string) => api.get<ReportDraft>(`/api/builder/${key}`),
  create: (payload: DraftCreatePayload) =>
    api.post<ReportDraft>("/api/builder", payload),
  update: (key: string, payload: DraftUpdatePayload) =>
    api.put<ReportDraft>(`/api/builder/${key}`, payload),
  remove: (key: string) => api.delete<void>(`/api/builder/${key}`),
  publish: (key: string, confirmBrokenViews = false) =>
    api.post<ReportDraft>(
      `/api/builder/${key}/publish`,
      confirmBrokenViews ? { confirm_broken_views: true } : undefined,
    ),
  /** The saved AI Home views the rule in ``aiConfig`` would break (writes nothing). */
  checkAiHomeViews: (key: string, aiConfig: AIConfig) =>
    api.post<AiHomeViewsCheck>(`/api/builder/${key}/ai-home/views/check`, { ai_config: aiConfig }),
  unpublish: (key: string) =>
    api.post<ReportDraft>(`/api/builder/${key}/unpublish`),
  /** List pre-update snapshots for a draft, newest first. */
  history: (key: string, limit = 50) =>
    api.get<DraftHistoryEntry[]>(
      `/api/builder/${key}/history?limit=${limit}`,
    ),
  /** List a report's persisted AI-insight executions, newest first. */
  aiExecutions: (key: string, limit = 20) =>
    api.get<AiExecutionListResponse>(
      `/api/builder/${key}/ai-executions?limit=${limit}`,
    ),
  /** Restore a draft to the state captured by a history snapshot. */
  revert: (key: string, historyId: string) =>
    api.post<ReportDraft>(
      `/api/builder/${key}/revert/${historyId}`,
    ),
};

// Templates (Plantillas) were removed from the builder UI in the
// 2026-05-18 audit revisit. The ``report_templates`` table still
// exists in postgres (seeded with built-ins at startup so future code
// paths could clone them), but no frontend surface consumes the
// templatesApi any more. If you're looking for the wrapper that used
// to live here, fetch it from the git history (``git log -p
// builder-api.ts``).

// ── Projects ─────────────────────────────────────────────

export const projectsApi = {
  list: () =>
    api.get<ProjectListResponse>("/api/builder/projects"),
  get: (slug: string) =>
    api.get<Project>(`/api/builder/projects/${slug}`),
  create: (payload: {
    slug: string;
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    dashboard_enabled?: boolean;
    maestros_enabled?: boolean;
    files_enabled?: boolean;
    master_files_config?: MasterFilesConfig;
    files_config?: FilesConfig;
  }) => api.post<Project>("/api/builder/projects", payload),
  update: (
    slug: string,
    payload: {
      slug?: string;
      name?: string;
      description?: string;
      color?: string;
      icon?: string;
      dashboard_enabled?: boolean;
      maestros_enabled?: boolean;
      files_enabled?: boolean;
      order_index?: number;
      master_files_config?: MasterFilesConfig;
      files_config?: FilesConfig;
      ai_home?: { time?: AiHomeTime };
    },
  ) => api.put<Project>(`/api/builder/projects/${slug}`, payload),
  remove: (slug: string) =>
    api.delete<void>(`/api/builder/projects/${slug}`),
  /**
   * Per-project connections (track B1). A connection carries the
   * NON-SECRET half of an upstream declaration (host, port, base url)
   * plus ``secret_ref`` — a POINTER to the credentials. Credential
   * VALUES never travel through this client in either direction.
   */
  connections: {
    list: (slug: string) =>
      api.get<ConnectionListResponse>(
        `/api/builder/projects/${slug}/connections`,
      ),
    create: (
      slug: string,
      payload: {
        name: string;
        kind: DataSourceKind;
        config?: Record<string, unknown>;
        secret_ref?: string;
      },
    ) =>
      api.post<ConnectionResponse>(
        `/api/builder/projects/${slug}/connections`,
        payload,
      ),
    update: (
      slug: string,
      connectionId: string,
      payload: {
        name?: string;
        config?: Record<string, unknown>;
        secret_ref?: string;
      },
    ) =>
      api.put<ConnectionResponse>(
        `/api/builder/projects/${slug}/connections/${connectionId}`,
        payload,
      ),
    remove: (slug: string, connectionId: string) =>
      api.delete<void>(
        `/api/builder/projects/${slug}/connections/${connectionId}`,
      ),
    /**
     * Preflight. Runs on an in-memory DuckDB — never opens the
     * project's .duckdb file, so testing a huge project is free.
     * A missing secret answers HTTP 400, not ``{ok: false}``.
     */
    test: (
      slug: string,
      connectionId: string,
      configOverride?: Record<string, unknown>,
    ) =>
      api.post<ConnectionTestResponse>(
        `/api/builder/projects/${slug}/connections/${connectionId}/test`,
        configOverride ? { config_override: configOverride } : {},
      ),
  },

  /**
   * Per-project data source (iter 23). Read the current config + audit
   * fields, set / clear the S3 URL declaration, or trigger a reload.
   * Kept in its own sub-namespace because the general ``update`` PUT
   * intentionally doesn't accept ``data_source`` — modifying it
   * needs the dedicated validator + can trigger a reload.
   */
  dataSource: {
    get: (slug: string) =>
      api.get<ProjectDataSourceResponse>(
        `/api/builder/projects/${slug}/data-source`,
      ),
    put: (
      slug: string,
      payload: {
        kind: DataSourceKind;
        url?: string;
        table?: string;
        alias?: string;
        /** Points at a ``connections`` row (track B1). */
        connection_id?: string;
        /**
         * Connector-specific config (postgres: host/port/database/
         * schema/query; api: records_path/pagination/auth/headers).
         * The backend connector for ``kind`` is the single validator,
         * so this client does not restate the per-kind rules.
         */
        [extra: string]: unknown;
      },
    ) =>
      api.put<ProjectDataSourceResponse>(
        `/api/builder/projects/${slug}/data-source`,
        payload,
      ),
    /**
     * Upload a CSV / Excel file to STAGE it as the project's dataset
     * (Plan C phase 4).
     *
     * Three separate acts, deliberately: this stages and describes the
     * file, ``put`` declares which staged file (and worksheet) the
     * project uses, and ``reload`` publishes it through the ordinary
     * atomic swap. Uploading alone never changes what the portal
     * serves.
     *
     * ``sheet`` is only meaningful for ``.xlsx``. Omitted, a
     * single-sheet workbook resolves itself and a multi-sheet one comes
     * back with ``sheet: null`` plus the full ``sheets`` list — sheet 1
     * is never assumed.
     */
    upload: (slug: string, file: File, sheet?: string) => {
      const form = new FormData();
      form.append("file", file);
      if (sheet) form.append("sheet", sheet);
      return api.post<ProjectUploadResponse>(
        `/api/builder/projects/${slug}/data-source/upload`,
        form,
      );
    },
    clear: (slug: string) =>
      api.delete<ProjectDataSourceResponse>(
        `/api/builder/projects/${slug}/data-source`,
      ),
    reload: (slug: string) =>
      api.post<ProjectDataSourceReloadResponse>(
        `/api/builder/projects/${slug}/reload`,
        {},
      ),
    /**
     * Surgical companion to ``reload``: re-runs ``CREATE OR REPLACE
     * VIEW`` for exactly one report without re-downloading the
     * project's source from S3. Used by the report Data tab's
     * "Apply view" button after editing the view SQL.
     */
    recreateView: (
      slug: string,
      reportKey: string,
      sourceName?: string,
    ) =>
      api.post<ReportViewRecreateResponse>(
        `/api/builder/projects/${slug}/reports/${reportKey}/recreate-view`
        + (sourceName ? `?source_name=${encodeURIComponent(sourceName)}` : ""),
        {},
      ),
  },

  /**
   * Per-project loader scheduler (iter 29). Moved from the portal's
   * admin Operations tab — each project's cron + recent jobs live here.
   * The actual execution path goes through the existing ``reload``
   * pipeline; this just adds cron-driven triggering + history.
   */
  /**
   * Persist the sidebar order of the project's reports (hub DnD).
   * ``keys`` must be EXACTLY the project's report keys in the desired
   * order — the backend 422s with a diff on any mismatch. The portal
   * picks the change up within ~60 s (no publish involved).
   */
  reorderReports: (slug: string, keys: string[]) =>
    api.put<{ keys: string[] }>(
      `/api/builder/projects/${encodeURIComponent(slug)}/report-order`,
      { keys },
    ),
  /**
   * Persist the sidebar order of ALL projects (home DnD).
   * ``slugs`` must be EXACTLY every project slug in the desired order —
   * the backend 422s with a diff on any mismatch. The portal sidebar
   * (``ORDER BY order_index, name``) picks the change up within ~60 s.
   */
  reorderProjects: (slugs: string[]) =>
    api.put<{ slugs: string[] }>("/api/builder/projects/order", { slugs }),
  loader: {
    /** Read schedule + state + last 5 jobs in one call (for UI render). */
    get: (slug: string) =>
      api.get<ProjectLoaderResponse>(
        `/api/builder/projects/${slug}/loader`,
      ),
    /** Persist a new schedule. Server-side cron + enabled cross-check. */
    update: (slug: string, payload: LoaderScheduleInput) =>
      api.put<ProjectLoaderResponse>(
        `/api/builder/projects/${slug}/loader`,
        payload,
      ),
    /** Manual trigger. Synchronous — server blocks until reload completes
     * (or fails). UI typically polls jobs after to confirm status.
     *
     * Iter 29.2 — when ``forceReap`` is true, force-abort any
     * in-flight pending/running jobs for this project before
     * enqueueing. Use ONLY when the admin knows a stuck job needs
     * to be killed; the default behaviour only reaps jobs older
     * than 15 min. */
    runNow: (slug: string, opts?: { forceReap?: boolean }) =>
      api.post<LoaderRunNowResponse>(
        `/api/builder/projects/${slug}/loader/run-now${
          opts?.forceReap ? "?force_reap=1" : ""
        }`,
        {},
      ),
    /** Paginated history. ``offset`` defaults to 0, ``limit`` to 25. */
    listJobs: (slug: string, offset = 0, limit = 25) =>
      api.get<LoaderJobListResponse>(
        `/api/builder/projects/${slug}/loader/jobs?offset=${offset}&limit=${limit}`,
      ),
    /** Iter 32 — per-element reconciliation detail for one job, backing
     * the badge → drawer. Returns every assertion row (golden vs engine
     * vs portal, status, delta) for the job, scoped to the project. */
    listReconciliations: (slug: string, jobId: string) =>
      api.get<LoaderReconciliationListResponse>(
        `/api/builder/projects/${slug}/loader/jobs/${jobId}/reconciliations`,
      ),
  },
};

// ── Loader / Scheduler types (iter 29) ───────────────────────────────


export interface LoaderScheduleInput {
  enabled: boolean;
  cron: string;
  timezone: string;
  mode: "incremental" | "reload" | "rebuild";
}

export interface LoaderState {
  last_run_at: string | null;
  last_status: string | null;
  next_run_at: string | null;
  current_job_id: string | null;
  last_error: string | null;
  consecutive_failures: number;
}

export interface LoaderJob {
  id: string;
  project_slug: string;
  mode: string;
  status: "pending" | "running" | "completed" | "failed";
  started_at: string | null;
  finished_at: string | null;
  error_summary: string | null;
  request_json: Record<string, unknown>;
  /** Iter 29.2 — live phase tracker, updated every ~5 s while
   * ``status='running'``. Shape:
   * ``{phase, ts, elapsed_ms, view?, index?, total?, ...}``.
   * Used by the UI to render WHICH step a long-running job is on. */
  progress_json: LoaderJobProgress | null;
  result_json: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

/** Iter 29.2 — known phase strings emitted by
 * ``service_projects.reload_project_data_source``. Treat unknown
 * values as a passthrough display string — do not switch exhaustively. */
export interface LoaderJobProgress {
  phase: string;
  ts: string;
  elapsed_ms: number;
  view?: string;
  index?: number;
  total?: number;
  row_count?: number;
  columns?: number;
  raw_table?: string;
  source_url?: string;
  view_count?: number;
  duration_ms?: number;
  rows?: number;
  error?: string;
  [extra: string]: unknown;
}

export interface ProjectLoaderResponse {
  schedule: LoaderScheduleInput;
  state: LoaderState;
  recent_jobs: LoaderJob[];
}

export interface LoaderRunNowResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface LoaderJobListResponse {
  jobs: LoaderJob[];
  total: number;
  offset: number;
  limit: number;
}

/** Iter 32 — one reconciliation assertion result for a job. */
export interface LoaderReconciliation {
  id: string;
  report_key: string;
  element_type: string; // kpi | chart | table
  element_id: string;
  /** "A_vs_B" (gate) | "B_vs_C_http" | "B_vs_C_cache" (portal). */
  layer: string;
  compare_kind: string;
  golden_value: string | null;
  actual_value: string | null;
  status: string; // pass | fail | error | exempt
  delta: number | null;
  message: string | null;
  created_at: string;
}

export interface LoaderReconciliationListResponse {
  reconciliations: LoaderReconciliation[];
  total: number;
}


// ── Global scheduler runtime status (admin) ─────────────────────────


export const schedulerApi = {
  /** Snapshot of the global scheduler loop's state. */
  status: () =>
    api.get<SchedulerStatus>("/api/builder/scheduler/status"),
  /** Process-level kill switch — flips the in-memory enabled flag.
   * Persisted intent lives in each project's ``loader_schedule``. */
  control: (enabled: boolean) =>
    api.post<SchedulerStatus>("/api/builder/scheduler/control", { enabled }),
};

// ── Operaciones cockpit (PR-6 builder reorg) ─────────────────────────

/** One cockpit row: project + schedule + rolling state. */
export interface OperationsProject {
  slug: string;
  name: string;
  schedule: LoaderScheduleInput;
  state: LoaderState;
}

export interface OperationsOverviewResponse {
  scheduler: SchedulerStatus;
  projects: OperationsProject[];
  /** Latest runs CROSS-project (newest first, capped at 20). */
  recent_jobs: LoaderJob[];
}

export const operationsApi = {
  /** The whole cockpit in one read-only call. Actions reuse the
   * per-project loader endpoints + the scheduler kill-switch. */
  overview: () =>
    api.get<OperationsOverviewResponse>("/api/builder/operations/overview"),
};

export interface SchedulerStatus {
  enabled: boolean;
  running: boolean;
  lease_holder: string | null;
  lease_expires_at: string | null;
  last_tick_at: string | null;
  active_job_id: string | null;
  poll_interval_seconds: number;
}

/**
 * Schema snapshot shape. Flat ``{col: type}`` for parquet kind,
 * nested ``{table: {col: type}}`` for duckdb_attach kind (multi-table
 * catalog). The UI branches on ``typeof entry`` to render both.
 */
export type ProjectDataSourceSchema = Record<
  string,
  string | Record<string, string>
>;

/**
 * Every ingestion kind the backend connector registry knows
 * (``builder.connectors.known_kinds()``). Keep in sync with the
 * pydantic Literal in ``schemas/projects.py`` — a mismatch surfaces as
 * a 500 AFTER a successful multi-minute load.
 */
export type DataSourceKind =
  | "parquet"
  | "duckdb_attach"
  | "postgres"
  | "api"
  | "csv"
  | "excel";

/**
 * Kinds whose bytes are a file the operator uploaded through the web
 * (Plan C phase 4) rather than something addressed with a URL. Mirrors
 * ``builder.connectors.UPLOAD_KINDS``.
 */
export const UPLOAD_DATA_SOURCE_KINDS: readonly DataSourceKind[] = [
  "csv",
  "excel",
];

export function isUploadKind(kind: DataSourceKind): boolean {
  return UPLOAD_DATA_SOURCE_KINDS.includes(kind);
}

/**
 * Kinds a ``connections`` row can describe — everything except the
 * uploaded-file ones, whose bytes are already on the tenant's own
 * volume and need no credential. ``service_connections.create_connection``
 * rejects the excluded two with a 400, so a ``ConnectionResponse`` can
 * never carry them.
 */
export type ConnectionKind = Exclude<DataSourceKind, "csv" | "excel">;

/** Result of ``POST /api/builder/projects/{slug}/data-source/upload``. */
export interface ProjectUploadResponse {
  upload_id: string;
  kind: DataSourceKind;
  ext: string;
  filename: string;
  size_bytes: number;
  uploaded_at: string;
  /** ``null`` while a multi-sheet workbook has no chosen worksheet. */
  sheet: string | null;
  /** Every worksheet in the workbook, in tab order. Empty for CSV. */
  sheets: string[];
  /** Rows DuckDB actually read. ``null`` only while a sheet is unchosen. */
  row_count: number | null;
  columns: Record<string, string>;
  delimiter: string;
}

export interface ConnectionResponse {
  id: string;
  /** `null` => SHARED with every project in this tenant (migration 033). */
  project_id: string | null;
  name: string;
  kind: ConnectionKind;
  config: Record<string, unknown>;
  /** A POINTER ('env:NAME' / 'vault:tenants/hp/...'), never a value. */
  secret_ref: string;
  created_at: string;
  updated_at: string;
}

export interface ConnectionListResponse {
  connections: ConnectionResponse[];
  total: number;
}

export interface ConnectionTestResponse {
  ok: boolean;
  detail: Record<string, unknown>;
}

export interface ProjectDataSourceResponse {
  data_source: Record<string, unknown>;
  data_source_loaded_at: string | null;
  data_source_rows: number;
  data_source_schema: ProjectDataSourceSchema;
}

export interface ProjectDataSourceReloadResponse {
  kind: DataSourceKind;
  /** Empty for kinds with no URL (postgres reads host/port/database). */
  url: string;
  rows: number;
  duration_ms: number;
  schema: ProjectDataSourceSchema;
  loaded_at: string;
  materialized_to: string;
  view_results: Record<string, string | null>;
  /** Free-form connector telemetry (track B1). */
  connector_detail?: Record<string, unknown>;
}

export interface ReportViewRecreateResponse {
  view_name: string;
  error: string | null;
  /**
   * Phase 2 — per-target outcome map for multi-source reports.
   * ``{target_name: error_or_None}``. Empty for single-source reports
   * (legacy shape preserved via ``view_name`` + ``error``).
   */
  recreated?: Record<string, string | null>;
}

// ── Validate ──────────────────────────────────────────────

export const validateApi = {
  /**
   * ``projectSlug`` is REQUIRED — ``null`` only when the server found
   * no project for the draft. The backend checks the report's columns
   * in the project's own DuckDB file, and since Plan A S4 there is no
   * shared file to fall back to: without the slug every validation, and
   * so every publish from the editor, failed with "preview needs a
   * project_slug" (staging, 2026-09-11).
   */
  run: (
    report_config: ReportConfig,
    dashboard_config: DashboardConfigV2 | Record<string, unknown>,
    projectSlug: string | null,
  ) =>
    api.post<ValidationResult>("/api/builder/validate", {
      report_config,
      dashboard_config,
      project_slug: projectSlug,
    }),
};

// ── DuckDB introspection ──────────────────────────────────

export const duckdbApi = {
  // ``projectSlug`` names the per-project DuckDB file these four read, and
  // since Plan A S4 there is no shared file behind it: a request without
  // it is refused ("preview needs a project_slug"). So the parameter is
  // REQUIRED — a call that forgets it is a tsc error, not a 500 at
  // runtime. It still accepts null/undefined: pass the slug the server
  // resolved for the draft (``draft.project_slug``) as it came, and its
  // absence is sent, not replaced.
  tables: (projectSlug: string | null | undefined) =>
    api.get<DuckDBTableInfo[]>(
      "/api/builder/tables" +
        (projectSlug ? `?project_slug=${encodeURIComponent(projectSlug)}` : ""),
    ),
  columns: (table: string, projectSlug: string | null | undefined) =>
    api.get<DuckDBColumnInfo[]>(
      `/api/builder/tables/${encodeURIComponent(table)}/columns` +
        (projectSlug ? `?project_slug=${encodeURIComponent(projectSlug)}` : ""),
    ),
  /**
   * Distinct non-null values for a column. Powers the Default / Locked
   * value autocomplete in the filter editor.
   */
  columnValues: (
    table: string,
    column: string,
    limit: number,
    projectSlug: string | null | undefined,
  ) =>
    api.get<string[]>(
      `/api/builder/tables/${encodeURIComponent(table)}/column-values?column=${encodeURIComponent(column)}&limit=${limit}` +
        (projectSlug ? `&project_slug=${encodeURIComponent(projectSlug)}` : ""),
    ),
  /** First N rows of a table — for the Data tab sample preview. */
  sample: (table: string, limit: number, projectSlug: string | null | undefined) =>
    api.get<Array<Record<string, unknown>>>(
      `/api/builder/tables/${encodeURIComponent(table)}/sample?limit=${limit}` +
        (projectSlug ? `&project_slug=${encodeURIComponent(projectSlug)}` : ""),
    ),
  /**
   * Download an S3 parquet, persist it under ``parquet_dir/{report_key}``,
   * and create (or append to) the DuckDB table. Replace wipes existing
   * rows; append adds to the current table.
   */
  loadFromS3: (
    table: string,
    body: {
      report_key: string;
      s3_url: string;
      mode: "replace" | "append";
    },
  ) =>
    api.post<{
      rows_loaded: number;
      parquet_path: string;
      duration_ms: number;
    }>(`/api/builder/tables/${encodeURIComponent(table)}/load`, body),
  /** Drop the table + delete its local parquet files. */
  drop: (table: string, reportKey?: string) =>
    api.delete<void>(
      `/api/builder/tables/${encodeURIComponent(table)}` +
        (reportKey ? `?report_key=${encodeURIComponent(reportKey)}` : ""),
    ),
};

// ── Preview (used in Fase 6) ──────────────────────────────

export interface PreviewRequest {
  report_config: ReportConfig;
  dashboard_config: DashboardConfigV2 | Record<string, unknown>;
  active_filters?: Record<string, string>;
  /** Iter 30 — target the per-project DuckDB file (``data/{slug}.duckdb``).
   * Omitting it makes the backend fall back to the legacy shared
   * ``analytics.duckdb``, which lacks per-project view-kind tables, so
   * multi-view table previews 500 with "table ... does not exist". */
  project_slug?: string | null;
  sort_by?: string | null;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
  /**
   * Multi-table dashboards expose more than one paginated table per
   * report (gating_market / gating_catalog / gating_sellers). Pass
   * the table item's id so the backend picks the right one. Omit
   * for single-table reports (dashboard_explorer).
   */
  table_id?: string | null;
  /** Required for ``/preview/drill`` only — drill_id to execute. */
  drill_id?: string;
  /** ``/preview/range-histogram`` only: the range filter and the
   *  interior bin boundaries the widget draws. */
  filter_key?: string;
  edges?: number[];
  /** Required for ``/preview/table-row-drill`` only — the clicked row's
   * ``row_drill_param`` cell value. Injected as the synthetic
   * ``row_drill`` filter the drill items reference via
   * ``{filter:row_drill}``. */
  row_value?: string | null;
}

export interface PreviewDrillResponse {
  drill_id: string;
  title?: string | null;
  description?: string | null;
  kpis: Array<Record<string, unknown>>;
  charts: Array<Record<string, unknown>>;
}

/**
 * Iter 31 — body of ``POST /api/builder/preview/table-row-drill``.
 * Mirrors the portal's ``ReportRowDrillData`` (``fetch_table_row_drill``
 * return shape): a scoped table + charts for the clicked row.
 */
export interface PreviewTableRowDrillResponse {
  drill_id: string;
  title?: string | null;
  description?: string | null;
  row_value: string;
  charts: Array<Record<string, unknown>>;
  table?:
    | {
        rows: Array<Record<string, unknown>>;
        total_count: number;
        error?: string;
      }
    | null;
}

/**
 * Iter 27 Phase 3b — body for ``POST /api/builder/preview/sql``.
 *
 * Returns the (template_sql, live_sql) pair for one inspector element
 * (KPI / filter / chart / table / text). ``element_id`` resolves
 * strictly per type: KPI label or ``i:N`` index; filter ``key``;
 * chart ``name``; table ``table_id`` (or ``"main"`` for the legacy
 * singular); text ``key``. No fallback to "the first one" — per the
 * global no-fallback rule the backend returns 404 on miss.
 */
export interface SqlPreviewRequest {
  report_config: ReportConfig;
  dashboard_config: DashboardConfigV2 | Record<string, unknown>;
  active_filters?: Record<string, string>;
  project_slug?: string | null;
  element_type: "kpi" | "filter" | "chart" | "table" | "text";
  element_id: string;
}

export interface SqlPreviewResponse {
  template_sql: string;
  live_sql: string | null;
  supported: boolean;
  note: string | null;
}

/** Body for ``POST /api/builder/preview/date-preset`` — resolves one
 * preset ``compute`` (optionally in a timezone) to a concrete window. */
export interface DatePresetPreviewRequest {
  compute: DatePresetCompute;
  /** IANA timezone; omit to resolve in the server default. */
  tz?: string;
}

export interface DatePresetPreviewResponse {
  start: string;
  end: string;
  days: number;
}

/** Options accepted by every preview call — effects pass their
 * generation's ``AbortSignal`` so a superseded request rejects with an
 * ``AbortError`` instead of landing a stale payload or a stale error. */
export interface PreviewCallOptions {
  signal?: AbortSignal;
}

// One deduper for the whole preview family (filters / kpis / charts /
// table / drill / row-drill / sql / date-preset). Keyed on url + body:
// an identical request arriving while the previous one is in flight
// reuses its promise (collapses React StrictMode's dev double-invoke to
// ONE network call per family); the entry clears on settle (a failure
// is immediately retryable — never sticky); each caller's own signal
// aborts only its subscription, and the underlying request is really
// cancelled once nobody is listening.
const previewDedupe = createInFlightDeduper();

function previewPost<T>(
  path: string,
  body: unknown,
  options?: PreviewCallOptions,
): Promise<T> {
  return previewDedupe<T>(
    `${path} ${JSON.stringify(body)}`,
    (signal) => api.post<T>(path, body, { signal }),
    options?.signal,
  );
}

export const previewApi = {
  filters: (body: PreviewRequest, options?: PreviewCallOptions) =>
    previewPost<Record<string, string[]>>(
      "/api/builder/preview/filters",
      body,
      options,
    ),
  rangeHistogram: (body: PreviewRequest, options?: PreviewCallOptions) =>
    previewPost<RangeHistogram>(
      "/api/builder/preview/range-histogram",
      body,
      options,
    ),
  kpis: (body: PreviewRequest, options?: PreviewCallOptions) =>
    previewPost<Array<Record<string, unknown>>>(
      "/api/builder/preview/kpis",
      body,
      options,
    ),
  table: (body: PreviewRequest, options?: PreviewCallOptions) =>
    previewPost<{ rows: Array<Record<string, unknown>>; total_count: number }>(
      "/api/builder/preview/table",
      body,
      options,
    ),
  charts: (body: PreviewRequest, options?: PreviewCallOptions) =>
    previewPost<Array<Record<string, unknown>>>(
      "/api/builder/preview/charts",
      body,
      options,
    ),
  /** Dynamic text blocks (engine ``fetch_texts``), as the portal's ``/texts``. */
  texts: (body: PreviewRequest, options?: PreviewCallOptions) =>
    previewPost<Array<Record<string, unknown>>>(
      "/api/builder/preview/texts",
      body,
      options,
    ),
  drill: (body: PreviewRequest, options?: PreviewCallOptions) =>
    previewPost<PreviewDrillResponse>(
      "/api/builder/preview/drill",
      body,
      options,
    ),
  tableRowDrill: (body: PreviewRequest, options?: PreviewCallOptions) =>
    previewPost<PreviewTableRowDrillResponse>(
      "/api/builder/preview/table-row-drill",
      body,
      options,
    ),
  sql: (body: SqlPreviewRequest, options?: PreviewCallOptions) =>
    previewPost<SqlPreviewResponse>("/api/builder/preview/sql", body, options),
  datePreset: async (
    body: DatePresetPreviewRequest,
    options?: PreviewCallOptions,
  ): Promise<DatePresetPreviewResponse> => {
    const raw = await previewPost<unknown>(
      "/api/builder/preview/date-preset",
      body,
      options,
    );
    // Strict shape validation (no-fabricated-data rule): ``request()``
    // types any 2xx payload as ``T`` even when the body is null, text, or
    // a partial object. Rejecting non-JSON 2xx globally in ``request()``
    // would risk every endpoint that answers 200 with an empty/loose body,
    // so the validation is scoped to this consumer — a malformed body must
    // surface as an error, never render as "undefined → undefined".
    if (!isResolvedDateWindow(raw)) {
      throw new HttpError(
        200,
        "Invalid date-preset preview response (expected {start, end, days})",
        raw,
      );
    }
    return raw;
  },
};

// ── Delivery (track B2) ────────────────────────────

/** Channels the backend registry knows. Keep in sync with
 * ``delivery_channels.registry.channel_names()`` — the create form's
 * select is built from this union. */
export type DeliveryChannel = "webhook" | "email" | "slack" | "teams";

export type DeliveryJobStatus =
  | "pending"
  | "running"
  | "sent"
  | "failed"
  | "dead";

/** A scheduled delivery. ``secret_ref`` is the POINTER to the
 * credential — the resolved value never crosses the API boundary. */
export interface DeliverySubscription {
  id: string;
  name: string;
  project_slug: string;
  report_key: string;
  channel: DeliveryChannel;
  target: Record<string, unknown>;
  /** The reusable channel credential (Plan C 2.3), or null for a
   * pre-033 subscription that still carries only `secret_ref`. */
  credential_id: string | null;
  credential_name: string | null;
  secret_ref: string | null;
  cron: string;
  timezone: string;
  locale: string;
  max_content_age_days: number;
  on_missing_content: "fail" | "trigger_watcher";
  options: Record<string, unknown>;
  enabled: boolean;
  last_fired_at: string | null;
  last_status: string | null;
  last_error: string | null;
  consecutive_failures: number;
  next_run_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DeliverySubscriptionInput {
  name: string;
  report_key: string;
  channel: DeliveryChannel;
  target: Record<string, unknown>;
  credential_id: string;
  secret_ref: string;
  cron: string;
  timezone: string;
  locale: string;
  max_content_age_days: number;
  on_missing_content: "fail" | "trigger_watcher";
  options: Record<string, unknown>;
  enabled: boolean;
}

export interface DeliveryJob {
  id: string;
  /** null for an origin='user_send' job (Plan F, F1). */
  subscription_id: string | null;
  subscription_name: string | null;
  project_slug: string;
  report_key: string;
  channel: DeliveryChannel;
  locale: string;
  idempotency_key: string;
  scheduled_for: string;
  status: DeliveryJobStatus;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string | null;
  snapshot_id: string | null;
  result: Record<string, unknown> | null;
  error_kind: string | null;
  error_summary: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DeliveryJobListResponse {
  jobs: DeliveryJob[];
  total: number;
}

/** Dry-run result. A FAILING test answers 424 with the real reason —
 * the point of the endpoint is that a missing credential surfaces here
 * and not at the first cron fire. */
export interface DeliveryTestResult {
  ok: boolean;
  channel: string;
  locale: string;
  snapshot_id: string;
  as_of_date: string;
  rendered_markdown: string;
  rendered_preview: Record<string, unknown>;
}

export interface DeliverySendNowResult {
  job_id: string;
  status: DeliveryJobStatus;
}

// ---------------------------------------------------------------------------
// User-send policy + audit (Plan F, F4) - tenant-admin only
// ---------------------------------------------------------------------------

export interface SendPolicy {
  configured: boolean;
  enabled: boolean;
  allowed_domains: string[];
  max_recipients: number;
  daily_quota_per_user: number | null;
  email_credential_id: string | null;
  email_credential_name: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface SendPolicyInput {
  enabled: boolean;
  allowed_domains: string[];
  max_recipients: number;
  daily_quota_per_user: number | null;
  email_credential_id: string | null;
}

export interface SendAuditRow {
  id: string;
  user_email: string;
  report_key: string;
  recipients: string[];
  locale: string;
  had_screenshot: boolean;
  had_ai: boolean;
  job_id: string | null;
  outcome: string;
  sent_at: string;
}

export interface SendAuditList {
  rows: SendAuditRow[];
  total: number;
}

export const sendPolicyApi = {
  get: () => api.get<SendPolicy>("/api/builder/send-policy"),
  put: (input: SendPolicyInput) =>
    api.put<SendPolicy>("/api/builder/send-policy", input),
  audit: (limit = 50, offset = 0) =>
    api.get<SendAuditList>(
      `/api/builder/send-policy/audit?limit=${limit}&offset=${offset}`,
    ),
};

export const deliveryApi = {
  listForProject: (slug: string) =>
    api.get<DeliverySubscription[]>(
      `/api/builder/projects/${slug}/deliveries`,
    ),
  create: (slug: string, payload: DeliverySubscriptionInput) =>
    api.post<DeliverySubscription>(
      `/api/builder/projects/${slug}/deliveries`,
      payload,
    ),
  update: (id: string, payload: DeliverySubscriptionInput) =>
    api.put<DeliverySubscription>(`/api/builder/deliveries/${id}`, payload),
  remove: (id: string) =>
    api.delete<void>(`/api/builder/deliveries/${id}`),
  test: (id: string) =>
    api.post<DeliveryTestResult>(`/api/builder/deliveries/${id}/test`),
  sendNow: (id: string) =>
    api.post<DeliverySendNowResult>(
      `/api/builder/deliveries/${id}/send-now`,
    ),
  jobsForSubscription: (id: string, limit = 10) =>
    api.get<DeliveryJobListResponse>(
      `/api/builder/deliveries/${id}/jobs?limit=${limit}`,
    ),
  recentJobs: (limit = 20) =>
    api.get<DeliveryJobListResponse>(
      `/api/builder/delivery/jobs?limit=${limit}`,
    ),
  retryJob: (jobId: string) =>
    api.post<DeliveryJob>(`/api/builder/delivery/jobs/${jobId}/retry`),
};
